// Create or edit a show. The same fields feed the RSS feed, so the hints
// say what podcast apps do with them.

import { useState, type FormEvent } from 'react';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { SelectField, TextArea, TextField } from '../ui/Field';
import { ErrorMessage } from '../ui/Feedback';
import type { Show } from '../../lib/shows';

// Apple Podcasts top-level categories (also accepted by Spotify).
export const CATEGORIES = [
  'Arts',
  'Business',
  'Comedy',
  'Education',
  'Fiction',
  'Government',
  'Health & Fitness',
  'History',
  'Kids & Family',
  'Leisure',
  'Music',
  'News',
  'Religion & Spirituality',
  'Science',
  'Society & Culture',
  'Sports',
  'Technology',
  'True Crime',
  'TV & Film',
];

export interface ShowFormValues {
  title: string;
  author: string;
  description: string;
  category: string;
  language: string;
  explicit: boolean;
  ownerEmail: string;
}

export function toFormValues(show?: Show): ShowFormValues {
  return {
    title: show?.title ?? '',
    author: show?.author ?? '',
    description: show?.description ?? '',
    category: show?.category ?? '',
    language: show?.language ?? 'en',
    explicit: show?.explicit ?? false,
    ownerEmail: show?.ownerEmail ?? '',
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  show?: Show;
  onSubmit: (values: ShowFormValues) => void;
  submitting: boolean;
  error: unknown;
}

export function ShowFormDialog({ open, onClose, show, onSubmit, submitting, error }: Props) {
  const [values, setValues] = useState<ShowFormValues>(() => toFormValues(show));
  const [titleError, setTitleError] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const set = <K extends keyof ShowFormValues>(key: K, value: ShowFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!values.title.trim()) {
      setTitleError('Give your show a name.');
      return;
    }
    setTitleError(undefined);
    const email = values.ownerEmail.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(undefined);
    onSubmit({ ...values, title: values.title.trim(), ownerEmail: email });
  };

  return (
    <Dialog open={open} onClose={onClose} title={show ? 'Edit show' : 'New show'}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <TextField
          label="Show name"
          value={values.title}
          onChange={(e) => set('title', e.target.value)}
          error={titleError}
          maxLength={200}
          autoFocus
        />
        <TextField
          label="Host or company"
          hint="Shown as the author in podcast apps."
          value={values.author}
          onChange={(e) => set('author', e.target.value)}
        />
        <TextField
          label="Owner email"
          type="email"
          hint="Apple and Spotify send ownership verification here. Published in your feed."
          value={values.ownerEmail}
          onChange={(e) => set('ownerEmail', e.target.value)}
          error={emailError}
        />
        <TextArea
          label="Description"
          hint="What listeners see before they subscribe."
          value={values.description}
          onChange={(e) => set('description', e.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Category"
            value={values.category}
            onChange={(e) => set('category', e.target.value)}
            options={[{ value: '', label: 'Choose…' }, ...CATEGORIES.map((c) => ({ value: c, label: c }))]}
          />
          <TextField
            label="Language"
            hint="e.g. en, es, fr"
            value={values.language}
            onChange={(e) => set('language', e.target.value)}
            maxLength={10}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={values.explicit} onChange={(e) => set('explicit', e.target.checked)} />
          Contains explicit content
        </label>
        {error !== null && error !== undefined && <ErrorMessage error={error} />}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {show ? 'Save changes' : 'Create show'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
